import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxButtonModule } from 'devextreme-angular';
import { PharmacyService } from 'src/app/shared/services/user/pharmacy.service';

export interface AddressVerificationAddress {
  line_1: string;
  line_2?: string;
  city: string;
  state: string;
  postal_code: string;
}

export type AddressVerificationResult =
  | { status: 'verified' }
  | { status: 'accepted'; address: AddressVerificationAddress }
  | { status: 'skipped' }
  | { status: 'idle' };

/**
 * AddressVerificationComponent
 *
 * Drop-in USPS address verification widget. Pass an address and set `verify`
 * to `true` to trigger a verification call. The component emits a result via
 * `(onResult)` once the user has made a decision:
 *
 *   - status='verified' → address is good, no correction needed
 *   - status='accepted' → user accepted USPS suggestion (corrected address included)
 *   - status='skipped'  → USPS couldn't find it, user chose to save anyway
 *   - status='idle'     → nothing happened yet
 *
 * Usage:
 *   <app-address-verification
 *     [address]="formData.delivery_address"
 *     [verify]="triggerVerify"
 *     (onResult)="handleVerificationResult($event)"
 *     (verifyChange)="triggerVerify = $event">
 *   </app-address-verification>
 */
@Component({
  selector: 'app-address-verification',
  templateUrl: './address-verification.component.html',
  styleUrls: ['./address-verification.component.scss']
})
export class AddressVerificationComponent implements OnChanges {
  /** The address to verify. */
  @Input() address: AddressVerificationAddress;

  /** Optional label shown in the panel header to identify which address is being verified. */
  @Input() label: string = '';

  /**
   * Set to `true` to kick off verification. The component resets it to
   * `false` via `(verifyChange)` once the call completes so two-way binding
   * works: `[(verify)]="triggerVerify"`.
   */
  @Input() verify: boolean = false;

  /** Emits the updated verify flag (supports two-way binding). */
  @Output() verifyChange = new EventEmitter<boolean>();

  /** Emits the final decision once the user has acted. */
  @Output() onResult = new EventEmitter<AddressVerificationResult>();

  // ── Internal state ──────────────────────────────────────────────────────
  verifying = false;
  showSuggestion = false;
  suggestedAddress: AddressVerificationAddress | null = null;
  addressNotFound = false;
  addressNotFoundMessage = '';
  invalidZip = false;
  invalidZipMessage = '';

  constructor(private pharmacyService: PharmacyService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['verify'] && this.verify && this.address) {
      this._runVerification();
    }
  }

  // ── Public helpers used by the template ─────────────────────────────────

  fieldsDiffer(field: keyof AddressVerificationAddress): boolean {
    if (!this.suggestedAddress || !this.address) return false;
    const current = (this.address[field] || '').toString().trim().toUpperCase();
    const suggested = (this.suggestedAddress[field] || '').toString().trim().toUpperCase();
    return current !== suggested && suggested !== '';
  }

  // ── User actions ─────────────────────────────────────────────────────────

  acceptSuggestion() {
    const accepted = { ...this.suggestedAddress } as AddressVerificationAddress;
    this._reset();
    this.onResult.emit({ status: 'accepted', address: accepted });
  }

  keepOriginal() {
    this._reset();
    this.onResult.emit({ status: 'verified' });
  }

  saveAnyway() {
    this._reset();
    this.onResult.emit({ status: 'skipped' });
  }

  editAddress() {
    this._reset();
    // No result emitted — the user wants to go back and correct the address.
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private _runVerification() {
    this.invalidZip = false;
    this.invalidZipMessage = '';
    this.addressNotFound = false;
    this.addressNotFoundMessage = '';
    this.showSuggestion = false;
    this.suggestedAddress = null;

    // Validate zip is exactly 5 digits before calling USPS
    const zip = (this.address.postal_code || '').trim();
    if (!/^\d{5}$/.test(zip)) {
      this.invalidZip = true;
      this.invalidZipMessage = zip.length === 0
        ? 'Postal code is required.'
        : 'Postal code must be exactly 5 digits (e.g. 85001).';
      this._done();
      return;
    }

    this.verifying = true;

    this.pharmacyService.verifyAddress({
      line_1: this.address.line_1,
      line_2: this.address.line_2,
      city: this.address.city,
      state: this.address.state,
      postal_code: this.address.postal_code
    }).subscribe(
      (result) => {
        this.verifying = false;
        this._done();
        const data = result?.data;

        if (data?.address_not_found) {
          this.addressNotFound = true;
          this.addressNotFoundMessage = data.error || 'USPS could not verify this address.';
        } else if (data?.correction && data?.corrected_address) {
          this.suggestedAddress = data.corrected_address as AddressVerificationAddress;
          this.showSuggestion = true;
        } else {
          // Valid — no correction needed
          this.onResult.emit({ status: 'verified' });
        }
      },
      () => {
        // Service unreachable — treat as verified so the user isn't blocked
        this.verifying = false;
        this._done();
        this.onResult.emit({ status: 'verified' });
      }
    );
  }

  private _done() {
    this.verifyChange.emit(false);
  }

  private _reset() {
    this.verifying = false;
    this.showSuggestion = false;
    this.suggestedAddress = null;
    this.addressNotFound = false;
    this.addressNotFoundMessage = '';
    this.invalidZip = false;
    this.invalidZipMessage = '';
    this._done();
  }
}

@NgModule({
  declarations: [AddressVerificationComponent],
  imports: [CommonModule, DxButtonModule],
  exports: [AddressVerificationComponent]
})
export class AddressVerificationModule {}
